import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Device, DeviceId} from '../../../../shared/models/device';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {Option} from '../../../../shared/models/option';
import {Tile, TileId} from '../../../../shared/models/tile';
import {DevicesService} from '../../../../core/services/devices.service';
import {TilesService} from '../../../../core/services/tiles.service';
import {TagService} from '../../../../core/services/tag.service';
import {TagAssignment} from '../../../../shared/models/tag';
import {ObjectType} from '../../../../shared/models/object.model';
import {concat, Observable, Subject, of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {ShadeToolsService} from '../../../../core/services/shade-tools.service';
import {SimpleStructure} from '../../../../shared/models/structure';
import {PillComponent} from '../../pill/pill.component';
import {PopoverSelectDevicesComponent} from '../../popover-multi-select/object-types/devices/popover-select-devices.component';
import {FormsModule} from '@angular/forms';
import {BulkCloneTileRowComponent} from './bulk-clone-tile-row/bulk-clone-tile-row.component';
import {SpinnerComponent} from '../../spinner/spinner.component';
import {NumericTileCreatorService} from '@features/formula-tile/creator/services/numeric-tile-creator.service';

export interface FindAndReplace {
  find: string;
  replace: string;
}

@Component({
  selector: 'motn-bulk-clone-tiles',
  templateUrl: './bulk-clone-tiles.component.html',
  styleUrls: ['./bulk-clone-tiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PillComponent,
    PopoverSelectDevicesComponent,
    FormsModule,
    BulkCloneTileRowComponent,
    SpinnerComponent,
  ],
})
export class BulkCloneTilesComponent implements OnInit, OnDestroy {
  cloneSourceDevice: Device;
  cloneDestinationDevice: Device;
  selectingDestinationDevice: boolean;

  findAndReplace$: Subject<FindAndReplace> = new Subject<FindAndReplace>();
  cloningActive$: Subject<boolean> = new Subject<boolean>();
  cloningActive: boolean;

  cloneMap: { [originalId: TileId]: Tile } = {};

  @Input() overlayPositions: ConnectedPosition[] = [{
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'top',
  }];

  @Input() tiles: Tile[];
  @Input() devices: Device[];

  @Output() done$: EventEmitter<any> = new EventEmitter<any>();

  findText: string = '';
  replaceText: string = '';


  constructor(
    private devicesService: DevicesService,
    private tilesService: TilesService,
    private tagService: TagService,
    private formulaTileCreator: NumericTileCreatorService,
    private shadeTools: ShadeToolsService,
  ) {
  }


  ngOnInit(): void {
    this.getTileCloneSourceDevice();

    this.cloningActive$.subscribe({
      next: (active) => {
        this.cloningActive = active;
      },
    });
  }


  ngOnDestroy() {
    this.findAndReplace$.complete();
    this.cloningActive$.complete();
  }


  get onlyFormulaTiles(): boolean {
    return !!this.tiles?.length && this.tiles.every(t => t.isFormulaTile());
  }


  get needsDestinationDevice(): boolean {
    return !!this.tiles?.some(t => !t.isFormulaTile() && !t.isCanvasTile());
  }


  startCloning() {
    if (this.needsDestinationDevice && !this.cloneDestinationDevice) {
      this.shadeTools.errorDialog(`A destination device must be selected before cloning.`);
      return;
    }

    this.cloningActive$.next(true);

    const clones: Observable<TileId>[] = Object.entries(this.cloneMap).map(
      ([originalId, t]) => this.createClone$(originalId, t).pipe(
        switchMap((newTileId) => this.copyTags$(originalId, newTileId).pipe(catchError(() => of(newTileId)))),
      ),
    );

    let successful: number = 0;

    concat(...clones).subscribe({
        next: () => {
          successful++;
        },
        error: (err) => {
          const reason = err?.message ? ` ${err.message}` : '';
          this.shadeTools.errorDialog(`The cloning operation failed. Before failing, ${successful} of the selected tiles were successfully cloned.${reason}`);

          if (successful > 0) {
            this.done$.emit();
          }
        },
        complete: () => {
          this.shadeTools.simpleOkDialog(`Success`, `All selected tiles were successfully cloned.`);
          this.done$.emit();
        },
      },
    );
  }


  private createClone$(originalId: TileId, clone: Tile): Observable<TileId> {
    const original = this.tiles.find(t => t.ID === originalId);

    if (original?.isFormulaTile()) {
      return this.formulaTileCreator.cloneFormulaTile$(original, clone);
    }

    if (original?.isCanvasTile()) {
      return this.createCanvasClone$(clone);
    }

    (clone.Structure as SimpleStructure).DeviceID = this.cloneDestinationDevice.ID;
    return this.tilesService.createTile$(clone);
  }


  private createCanvasClone$(clone: Tile): Observable<TileId> {
    const childIds = clone.getCanvasChildIds();

    return this.tilesService.createTile$(clone).pipe(
      switchMap((newTileId) => childIds.length
        ? this.tilesService.replaceChildrenOfTile$(newTileId, childIds).pipe(map(() => newTileId))
        : of(newTileId)),
    );
  }


  private copyTags$(originalId: TileId, newTileId: TileId): Observable<TileId> {
    return this.tilesService.fetchTile$(originalId).pipe(
      switchMap((original) => {
        if (!original.Tags?.length) {
          return of(newTileId);
        }

        const assignments: TagAssignment[] = original.Tags.map(tag => ({TagID: tag.ID, Value: tag.Value}));
        return this.tagService.assignTags$(ObjectType.TILE, newTileId, assignments).pipe(
          tap(() => this.tilesService.updateTileTags(newTileId, original.Tags)),
          map(() => newTileId),
        );
      }),
    );
  }


  /**
   * Receives updated Tile models as the clones are updated
   *
   * @param originalId
   * @param clone
   */
  onCloneUpdate(originalId: TileId, clone: Tile) {
    this.cloneMap[originalId] = clone;
  }


  openCloneTilesDeviceMenu() {
    this.selectingDestinationDevice = true;
  }


  closeCloneTilesDeviceMenu(selectedOption?: Option) {
    this.selectingDestinationDevice = false;

    if (selectedOption) {
      this.cloneDestinationDevice = this.devices.find(d => d.ID === selectedOption.id);
    }
  }


  findAndReplace() {
    const text: FindAndReplace = {
      find: this.findText,
      replace: this.replaceText,
    };

    if (text.find === '') {
      this.shadeTools.errorDialog('The "find" value cannot be empty.');
      return;
    }

    if (text.find === text.replace) {
      this.shadeTools.errorDialog('The "find" and "replace" values are the same.');
      return;
    }

    this.findAndReplace$.next(text);

    this.findText = '';
    this.replaceText = '';
  }


  /**
   * Basic assumptions/prerequisites before calling this:
   *  1.  Object type is tiles
   *  2.  Tiles already exist at this.tiles
   *  3.  this.tiles is not empty
   *  4.  The tiles stored at this.tiles all use same device
   *
   * @private
   */
  private getTileCloneSourceDevice() {
    if (!this.tiles || this.tiles.length === 0) {
      return;
    }

    const anyTile: Tile | undefined = this.tiles.find(t => !t.isFormulaTile() && !t.isCanvasTile());
    if (!anyTile) {
      return;
    }

    const deviceId: DeviceId = anyTile.getDeviceIds()[0];

    this.devicesService.getUserDevices$().subscribe({
      next: (devices) => {
        this.cloneSourceDevice = devices.find((d) => d.ID === deviceId);
        this.devices = devices.filter(d => d.Type === this.cloneSourceDevice.Type);
      },
    });
  }
}
